import { Link } from 'react-router';
import { useSuspenseQuery } from '@tanstack/react-query';
import type { SiteSettings } from '@ceo/shared';
import { useTranslation } from 'react-i18next';
import { useLocale, useLocalePath } from '@/lib/locale';
import { telHref } from '@/lib/phone';
import { q } from '@/lib/queries';
import { Icon } from '@/components/ui/Icon';
import styles from './Footer.module.css';

const SOCIAL_ICON: Record<
  SiteSettings['socials'][number]['network'],
  { icon: string; label: string }
> = {
  facebook: { icon: 'facebook-f', label: 'Facebook' },
  instagram: { icon: 'instagram', label: 'Instagram' },
  linkedin: { icon: 'linkedin-in', label: 'LinkedIn' },
  youtube: { icon: 'youtube', label: 'YouTube' },
  x: { icon: 'x-twitter', label: 'X' },
  tiktok: { icon: 'tiktok', label: 'TikTok' },
};

export function Footer() {
  const { t } = useTranslation();
  const to = useLocalePath();
  const { settings } = useSuspenseQuery(q.settings(useLocale())).data;

  const groups = [
    [
      ['footer.home', '/'],
      ['footer.about', '/company-background'],
      ['footer.clients', '/our-clients'],
      ['footer.staff', '/our-staff'],
    ],
    [
      ['footer.services', '/services'],
      ['footer.events', '/events'],
      ['footer.contact', '/contact-us'],
      ['footer.dataProtection', '/data-protection'],
    ],
  ] as const;

  return (
    <footer className={styles.footer}>
      <div className={`container ${styles.grid}`}>
        <section className={styles.brandCol}>
          <Link to={to('/')} className={styles.brand} aria-label={t('nav.home')}>
            <img src="/media/ceo_logo.png" alt={t('brand')} width={259} height={108} />
          </Link>
          {settings.metaDescription && <p className={styles.tagline}>{settings.metaDescription}</p>}
          <h2 className="visually-hidden">{t('footer.social')}</h2>
          <ul className={styles.social}>
            {settings.socials.map((s, i) => (
              <li key={`${i}-${s.url}`}>
                <a
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={SOCIAL_ICON[s.network].label}
                >
                  <Icon name={SOCIAL_ICON[s.network].icon} brand />
                </a>
              </li>
            ))}
          </ul>
        </section>

        <section>
          <h2 className={styles.title}>{t('footer.links')}</h2>
          <div className={styles.linkCols}>
            {groups.map((links, i) => (
              <ul key={i} className={styles.links}>
                {links.map(([key, path]) => (
                  <li key={key}>
                    <Link to={to(path)}>{t(key)}</Link>
                  </li>
                ))}
              </ul>
            ))}
          </div>
        </section>

        <section>
          <h2 className={styles.title}>{t('footer.contactTitle')}</h2>
          <ul className={styles.contact}>
            <li>
              <Icon name="location-dot" className={styles.icon} />
              <span>{settings.footerAddress}</span>
            </li>
            {settings.phones.length > 0 && (
              <li>
                <Icon name="phone" className={styles.icon} />
                <span>
                  {settings.phones.map((p, i) => (
                    <a key={`${i}-${p}`} href={telHref(p)}>
                      {p}
                      {i < settings.phones.length - 1 ? '\n' : ''}
                    </a>
                  ))}
                </span>
              </li>
            )}
            <li>
              <Icon name="envelope" className={styles.icon} />
              <a href={`mailto:${settings.email}`}>{settings.email}</a>
            </li>
          </ul>
        </section>
      </div>

      <div className={styles.bottom}>
        <div className={`container ${styles.bottomInner}`}>
          <p>{settings.copyright}</p>
        </div>
      </div>
    </footer>
  );
}
